import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBlogArticles, useBlogCategories } from '@/hooks/use-blog-api';
import { blogCategoryLabel } from '@/lib/blog-categories';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, BookOpen, Calendar, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const Blog = () => {
  const { isRTL, t } = useLanguage();
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useBlogArticles({ category: category || undefined, search: search || undefined, page, limit: 9 });
  const { data: categories } = useBlogCategories();

  const articles = (data as any)?.articles || [];
  const total = (data as any)?.total || 0;
  const totalPages = Math.ceil(total / 9);

  const blogStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema([
        { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
        { name: isRTL ? 'المدونة' : 'Blog', url: 'https://hudanlilmuttaqeen.com/blog' },
      ]),
      // P5.4.6 — ItemList of BlogPosting for the listing page
      {
        "@type": "ItemList",
        "itemListElement": articles.map((a: { slug?: string; name: string; title?: string; title_ar?: string }, i: number) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "BlogPosting",
            "headline": isRTL ? (a.title_ar || a.title) : a.title,
            "url": `https://hudanlilmuttaqeen.com/blog/${a.slug || a.name}/`,
            "inLanguage": isRTL ? 'ar' : 'en',
          },
        })),
      },
    ],
  };

  return (
    <Layout>
      <SEO
        titleKey="seo.blog.title"
        descriptionKey="seo.blog.description"
        keywordsKey="seo.blog.keywords"
        structuredData={blogStructuredData}
      />

      <PageHeader
        title="Blog & Articles"
        titleAr="المدونة والمقالات"
        subtitle="Insights, tips, and stories about Quran education"
        subtitleAr="رؤى ونصائح وقصص حول تعليم القرآن"
        size="sm"
      >
        <div className="relative max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-100/60" aria-hidden="true" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={isRTL ? 'ابحث في المقالات...' : 'Search articles...'}
            className="ps-9 bg-white/10 border-white/20 text-white placeholder:text-emerald-100/50 focus:bg-white/15"
            aria-label={isRTL ? 'البحث في المقالات' : 'Search articles'}
            type="search"
          />
        </div>
      </PageHeader>

      <div className="container py-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Category Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl overflow-x-auto">
          <button
            type="button"
            onClick={() => { setCategory(''); setPage(1); }}
            aria-pressed={!category}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              !category ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {isRTL ? 'الكل' : 'All'}
          </button>
          {((categories as string[]) || []).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { setCategory(c); setPage(1); }}
              aria-pressed={category === c}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                category === c ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {blogCategoryLabel(c, isRTL)}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden animate-pulse">
                <div className="h-48 bg-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article: any, i: number) => (
                <motion.div
                  key={article.name}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={`/blog/${article.slug}`} className="group block">
                    <div className="rounded-xl border bg-card overflow-hidden transition-shadow hover:shadow-lg">
                      {article.cover_image ? (
                        <div className="h-48 overflow-hidden">
                          <img
                            src={article.cover_image}
                            alt={isRTL ? article.title_ar || article.title : article.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/30 dark:to-emerald-800/20 flex items-center justify-center">
                          <BookOpen className="h-12 w-12 text-emerald-300" />
                        </div>
                      )}
                      <div className="p-5 space-y-3">
                        <div className="flex items-center gap-2">
                          {article.category && (
                            <Badge variant="secondary" className="text-xs">{blogCategoryLabel(article.category, isRTL)}</Badge>
                          )}
                          {article.is_featured ? (
                            <Badge className="text-xs bg-amber-500/10 text-amber-600 border-amber-200">
                              {isRTL ? 'مميز' : 'Featured'}
                            </Badge>
                          ) : null}
                        </div>
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {isRTL ? (article.title_ar || article.title) : article.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {isRTL ? (article.excerpt_ar || article.excerpt) : article.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                          {article.published_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(article.published_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {article.views != null && (
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {article.views}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
                <span className="text-sm text-muted-foreground px-3">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={BookOpen}
            title={isRTL ? 'لا توجد مقالات' : 'No articles found'}
            description={isRTL ? 'جرب البحث بكلمات مختلفة' : 'Try searching with different keywords'}
          />
        )}
      </div>
    </Layout>
  );
};

export default Blog;
